import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormInput, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCountryDataList } from 'countries-list';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const countryDataList = getCountryDataList();

const countryOptions = countryDataList.map(country => ({ code: country.iso2, label: country.name }));

export function OrganizationForm ({ data, onChangeField, onCompleted }) {
    const formSchema = z.object({
        country: z.string().min(1, i18next.t('form_validations.organization.country_required')),
        organizationName: z.string().min(1, i18next.t('form_validations.organization.organization_name_required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            country: data.country || '',
            organizationName: data.organizationName || ''
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        country: debounce(() => form.trigger('country'), 600),
        organizationName: debounce(() => form.trigger('organizationName'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <Form {...form}>
            <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('organizations.name').toLowerCase()}</FormLabel>
                        <FormControl>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    field.onChange(e);
                                    onChangeField('organizationName')(e);
                                    debouncedTriggers.current.organizationName();
                                }}
                                placeholder={i18next.t('organizations.name')}
                            />
                        </FormControl>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('common.country')}</FormLabel>
                        <FormControl>
                            <Select
                                onValueChange={val => {
                                    field.onChange(val);
                                    onChangeField('country')(val);
                                    debouncedTriggers.current.country();
                                }}
                                value={field.value}
                            >
                                <SelectTrigger className="tw-ml-0">
                                    <SelectValue placeholder={i18next.t('organizations.select_a_country')}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {countryOptions.map(({ code, label }) => (
                                        <SelectItem key={code} value={code}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                    </FormItem>
                )}
            />
        </Form>
    );
}
